import React, { useCallback, useRef, useState } from 'react';
import { getOffset, cn } from '@express-labs/raven-tools';
import s from './Image.scss';
import RenderImageTag from './RenderImageTag';

export interface ImageSrcSet {
  breakPoint: number;
  src: string;
}

export type ImageProps = {
  alt?: string;
  className?: string;
  isLazyLoading?: boolean;
  onError?: (ev: React.SyntheticEvent<HTMLImageElement>) => void;
  onImageClick?: (ev: React.MouseEvent<HTMLElement>) => void;
  onLoad?: (ev: React.SyntheticEvent<HTMLImageElement>) => void;
  responsive?: boolean;
  scale?: boolean;
  src?: string;
  srcSet?: ImageSrcSet[];
  tabIndex?: number;
  zoom?: boolean;
};

export function getSortedSrcSet(srcSet?: ImageSrcSet[]): ImageSrcSet[] {
  return srcSet?.length
    ? srcSet.sort((a, b) => a.breakPoint - b.breakPoint)
    : [];
}

/**
 * This component adds special functionality to an image such zooming the
 * image, scaling the image, or making an image a link with a callback.  Much
 * of this functionality is used on the product detail page, a.k.a. PDP page.
 *
 * If you don't need the special functionality provided by this component,
 * avoid the added overhead and stick with the good ol' `<img />` tag.
 *
 * **Hint:** the Raven-UI [Brander](/ui/Brander) has a class called
 * "responsive" to make <img /> tag images shrink with the parent container
 * shrinks (responsive image)
 */
const Image = ({
  alt = 'Product image',
  className,
  isLazyLoading = false,
  onError,
  onImageClick,
  onLoad,
  responsive = false,
  scale = false,
  src,
  srcSet,
  tabIndex = 0,
  zoom = false,
}: ImageProps) => {
  const [isError, setIsError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const sortedSrcSet: ImageSrcSet[] = getSortedSrcSet(srcSet);

  const handleOnLoad = useCallback(
    (ev: React.SyntheticEvent<HTMLImageElement>) => {
      setIsLoaded(true);
      setIsLoading(false);
      if (typeof onLoad === 'function') {
        onLoad(ev);
      }
    },
    [onLoad]
  );

  const handleOnError = useCallback(
    (ev: React.SyntheticEvent<HTMLImageElement>) => {
      setIsError(true);
      setIsLoading(false);
      if (typeof onError === 'function') {
        onError(ev);
      }
    },
    [onError]
  );

  const handleImageClick = useCallback(
    (ev: React.MouseEvent<HTMLElement, MouseEvent>) => {
      ev.preventDefault();
      if (typeof onImageClick === 'function') {
        onImageClick(ev);
      }
    },
    [onImageClick]
  );

  const handleMouseOver = useCallback(() => {
    setIsZoomed(true);
  }, []);

  const handleMouseOut = useCallback(() => {
    setIsZoomed(false);
  }, []);

  const handleMouseMove = useCallback(
    (ev: React.MouseEvent<HTMLDivElement>) => {
      const offset = getOffset<HTMLDivElement>(containerRef.current);
      if (
        typeof containerRef.current?.offsetWidth === 'number' &&
        typeof imageRef?.current?.offsetHeight === 'number' &&
        offset !== null
      ) {
        const x =
          ((ev.pageX - offset.left) / containerRef.current?.offsetWidth) * 100;
        const y =
          ((ev.pageY - offset.top) / imageRef.current?.offsetHeight) * 100;

        imageRef.current.style.cssText = `transform-origin: ${x}% ${y}%;`;
      }
    },
    []
  );

  const handleKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLElement>) => {
      if (
        typeof onImageClick === 'function' &&
        (ev.code === 'Enter' || ev.code === 'Space')
      ) {
        onImageClick(ev as unknown as React.MouseEvent<HTMLElement>);
      }
    },
    [onImageClick]
  );

  const classes: string[] = [className || ''];
  if (responsive) classes.push(s.responsive);

  if (zoom) {
    if (isZoomed) classes.push(s.zoom);

    return (
      <div ref={containerRef} className={s.zoomContainer}>
        <div
          role="button"
          className={s.zoomAnchor}
          onKeyDown={handleKeyDown}
          onClick={handleImageClick}
          onMouseMove={handleMouseMove}
          onMouseOut={handleMouseOut}
          onMouseOver={handleMouseOver}
          onFocus={handleMouseOver}
          onBlur={handleMouseOut}
          tabIndex={tabIndex}
        >
          <RenderImageTag
            alt={alt}
            className={cn(classes)}
            isError={isError}
            isLoaded={isLoaded}
            isLoading={isLoading}
            onError={handleOnError}
            onLoad={handleOnLoad}
            ref={imageRef}
            sortedSrcSet={sortedSrcSet}
            src={src}
            srcSet={srcSet}
            loading={isLazyLoading ? 'lazy' : 'eager'}
          />
        </div>
      </div>
    );
  }

  if (scale) classes.push(s.scaleImage);

  if (onImageClick) {
    return (
      <div
        role="button"
        tabIndex={tabIndex}
        onKeyDown={handleKeyDown}
        onClick={handleImageClick}
        className={`${s.cursor} ${s.responsiveAnchor}`}
      >
        <RenderImageTag
          alt={alt}
          className={cn(classes)}
          isError={isError}
          isLoaded={isLoaded}
          isLoading={isLoading}
          onError={handleOnError}
          onLoad={handleOnLoad}
          ref={imageRef}
          sortedSrcSet={sortedSrcSet}
          src={src}
          srcSet={srcSet}
          loading={isLazyLoading ? 'lazy' : 'eager'}
        />
      </div>
    );
  }

  return (
    <RenderImageTag
      alt={alt}
      className={cn(classes)}
      isError={isError}
      isLoaded={isLoaded}
      isLoading={isLoading}
      onError={handleOnError}
      onLoad={handleOnLoad}
      ref={imageRef}
      sortedSrcSet={sortedSrcSet}
      src={src}
      srcSet={srcSet}
      loading={isLazyLoading ? 'lazy' : 'eager'}
    />
  );
};

Image.displayName = 'Image';
export default Image;
